export const formatDate = (dateString) => {
  const date = new Date(dateString);
  const month = String(date.getMonth() + 1).padStart(2, "0"); // 월이 한 자리면 앞에 0 추가
  const day = String(date.getDate()).padStart(2, "0"); // 일이 한 자리면 앞에 0 추가
  return `${month}/${day}`; // 원하는 형식으로 반환
};

export const showType = (assignmentType, sectionType) => {
  if (assignmentType === "vocabulary") {
    return "";
  }
  if (assignmentType === "paragraph") {
    return sectionType;
  }
  const typeMap = {
    analysis: {
      keypoints: "핵심정리",
      reading: "지문읽기",
      words: "단어학습",
      syntax_analysis: "구문분석",
      flow: "흐름정리",
      sentence_test: "문장테스트",
    },
    comprehensive: {
      comprehensive: "내용이해 워크북",
      keyword: "단어 테스트",
      understanding: "서술형 연습",
      ox: "OX 퀴즈",
      translation: "해석 연습",
      summary: "요약 연습",
      scramble: "페러프레이징",
    },
    sentence: {
      blank: "빈칸 채우기",
      scramble: "스크램블",
      composition: "영작",
      translation: "해석",
      dictation: "받아쓰기",
      comprehensive: "종합",
    },
  };
  return typeMap[assignmentType]?.[sectionType] || sectionType;
};

export const getContentHeight = (isMobile = false) => {
  if (isMobile) return "calc(100vh - 80px)";
  return "calc(100vh - 140px)";
};

export const splitIntoChunks = (text, maxWords = 30) => {
  const chunks = [];

  const words = text.split(" ");
  for (let j = 0; j < words.length; j += maxWords) {
    chunks.push(words.slice(j, j + maxWords).join(" "));
  }

  return chunks;
};

export const boldText = ({ text }) => {
  if (!text) {
    return null;
  }

  const pattern = /--\*\*(.*?)\*\*--/g;
  const parsed = [];
  let lastIndex = 0;
  let match;

  while ((match = pattern.exec(text)) !== null) {
    parsed.push(text.substring(lastIndex, match.index));
    parsed.push(<strong key={parsed.length}>{match[1]}</strong>);
    lastIndex = pattern.lastIndex;
  }

  parsed.push(text.substring(lastIndex));

  return <>{parsed}</>;
};

export const convertCircledNumberToDigit = (str) => {
  const unicodeOffset = "①".charCodeAt(0) - 1; // ①의 코드 포인트(9312)에서 1을 뺀 값
  return str.charCodeAt(0) - unicodeOffset;
};

export const convertDigitToCircledNumber = (num) => {
  if (num < 1 || num > 10) {
    throw new Error("Input must be between 1 and 10");
  }
  const unicodeOffset = "①".charCodeAt(0) - 1; // ①의 코드 포인트(9312)에서 1을 뺀 값
  return String.fromCharCode(unicodeOffset + num);
};

export const applyUnderline = (text) => {
  const parts = text.split(/(<u>.*?<\/u>)/g); // '<u>텍스트</u>' 부분을 분리

  return parts.map((part, index) => {
    if (part.startsWith("<u>") && part.endsWith("</u>")) {
      return (
        <u key={index}>
          <strong>{part.replace(/<\/?u>/g, "")}</strong>
        </u>
      ); // '<u>'와 '</u>' 제거 후 JSX 변환
    }
    return part; // 일반 텍스트는 그대로 반환
  });
};

export const getTextByLabel = (text, label) => {
  const regex = new RegExp(`--${label} (.*?)--`);
  const match = text.match(regex);
  return match ? match[1] : null; // 매칭된 그룹이 있으면 반환, 없으면 null 반환
};

export const fillColorAtSubstance = (text, color) => {
  const match = text.match(/\[(.*?)\]/); // [] 안의 텍스트 찾기
  if (match) {
    const beforeBracket = text.slice(0, match.index);
    const bracketedText = match[0]; // 대괄호 포함된 전체 문자열
    const afterBracket = text.slice(match.index + bracketedText.length);
    return (
      <>
        <span
          style={{
            color: color,
            fontWeight: "bold",
          }}
        >
          {beforeBracket}
          {bracketedText}
        </span>
        {afterBracket}
      </>
    );
  }
  return text;
};

export const formatFullDateString = ({ dateString, defaultString }) => {
  if (!dateString) return defaultString;
  const date = new Date(dateString);
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0"); // 월이 한 자리면 앞에 0 추가
  const day = String(date.getDate()).padStart(2, "0"); // 일이 한 자리면 앞에 0 추가
  const hours = String(date.getHours()).padStart(2, "0"); // 시간이 한 자리면 앞에 0 추가
  const minutes = String(date.getMinutes()).padStart(2, "0"); // 분이 한 자리면 앞에 0 추가
  const seconds = String(date.getSeconds()).padStart(2, "0"); // 초가 한 자리면 앞에 0 추가
  return `${year}-${month}-${day} ${hours}:${minutes}:${seconds}`; // 원하는 형식으로 반환
};

export const showGrade = (grade) => {
  const gradeMap = {
    1: "초1",
    2: "초2",
    3: "초3",
    4: "초4",
    5: "초5",
    6: "초6",
    7: "중1",
    8: "중2",
    9: "중3",
    10: "고1",
    11: "고2",
    12: "고3",
  };

  return gradeMap[grade] || grade;
};

export const roundScoreValue = (value) => {
  if (value === null || value === undefined || value === "") {
    return null;
  }

  const parsedValue = Number(value);
  if (!Number.isFinite(parsedValue)) {
    return null;
  }

  return Math.round(parsedValue * 100) / 100;
};

export const formatScore = (value) => {
  const roundedScore = roundScoreValue(value);
  if (roundedScore === null) {
    return "";
  }

  return roundedScore.toFixed(2).replace(/\.00$/, "").replace(/(\.\d)0$/, "$1");
};

/**
 * Normalizes English text for answer comparison by:
 * - Converting to lowercase
 * - Removing all spaces
 * - Removing all punctuation marks
 */
export const normalizeEnglishAnswer = (text) => {
  if (!text) return "";
  return text
    .toLowerCase()
    .replace(/[\s]/g, "") // Remove all whitespace
    .replace(/[.,!?;:'"()\-–—…]/g, ""); // Remove common punctuation
};

/**
 * Normalizes sentence-style answers for robust exact-match checks.
 * - Applies Unicode normalization to reduce visually identical character mismatches
 * - Collapses repeated whitespace
 * - Removes spaces right before punctuation
 * - Optionally lowercases text for case-insensitive comparison
 */
export const normalizeSentenceAnswer = (text, { ignoreCase = true } = {}) => {
  if (!text) return "";

  const normalized = text
    .normalize("NFKC")
    .replace(/[’‘]/g, "'")
    .replace(/[“”]/g, '"')
    .replace(/\s+/g, " ")
    .replace(/\s+([,.!?;:])/g, "$1")
    .trim();

  return ignoreCase ? normalized.toLowerCase() : normalized;
};

/**
 * Parses paragraph:summary_blank markup such as
 * "prefix --**a**　　　-- suffix" into displayable sentence parts.
 */
export const parseParagraphSummaryBlank = (text) => {
  const source = String(text || "");
  const blankMatch = source.match(/--([\s\S]*?)--/);

  if (!blankMatch) {
    return { prefix: source, hint: "", suffix: "" };
  }

  const hintMatch = blankMatch[1].match(/\*\*(.*?)\*\*/);

  return {
    prefix: source.slice(0, blankMatch.index),
    hint: hintMatch ? hintMatch[1] : "",
    suffix: source.slice(blankMatch.index + blankMatch[0].length),
  };
};
